import type { Group, AnimationClip } from 'three'
import { WebGLRenderer } from 'three'
import { GLTFLoader as PatchedGLTFLoader } from '@/lib/threejs/PatchedGLTFLoader'
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader'
import { KTX2Loader } from 'three/examples/jsm/loaders/KTX2Loader'
import type { GLTF } from 'three/examples/jsm/loaders/GLTFLoader'
import type { Analytics } from '@/plugins/analytics'

const wasmErrorMessages = ['out of bounds', 'function signature mismatch', 'is not a function'] as const
export const isDracoWASMError = (error: any): boolean =>
  error.type === 'error' && error.id && error.error && wasmErrorMessages.some(message => error.error.includes?.(message))

// Lazily create the loaders and renderers
export const getGltfLoader = (() => {
  let usingJSDecoder = false
  let gltfLoader: PatchedGLTFLoader
  let dracoLoader: DRACOLoader
  let ktx2Loader: KTX2Loader

  return (needJSDecoder: boolean = false) => {
    if (!gltfLoader) {
      gltfLoader = new PatchedGLTFLoader()

      // DRACOLoader has a dispose() method. Since we just use a single instance, avoid calling it for now
      dracoLoader = new DRACOLoader()
      dracoLoader.setDecoderPath('/js/draco_2_39_0/')
      gltfLoader.setDRACOLoader(dracoLoader)

      const featureDetectionRenderer = new WebGLRenderer()

      // ktx2Loader creates a number of WebWorkers for loading (up to 4 by default)
      // Since we only a single instance, we can avoid needing to call dispose() on it for now
      ktx2Loader = new KTX2Loader()
      ktx2Loader.setTranscoderPath('/js/basis/').detectSupport(featureDetectionRenderer)
      gltfLoader.setKTX2Loader(ktx2Loader)
    }

    if (needJSDecoder && !usingJSDecoder) {
      // Switch to JS loader
      dracoLoader.dispose()
      dracoLoader = new DRACOLoader()
      dracoLoader.setDecoderPath('/js/draco_2_39_0/')
      dracoLoader.setDecoderConfig({ type: 'js' })
      gltfLoader.setDRACOLoader(dracoLoader)
      usingJSDecoder = true
    }

    return gltfLoader
  }
})()

interface Options {
  analytics: typeof Analytics | null
  modelId: number
  uuid: string | null
  useJsDecoder: boolean
}

export interface ModelResponse {
  scene: Group
  animations: AnimationClip[]
}

export const loadGltf = (
  url: string,
  onProgress: (event: ProgressEvent) => void,
  options: Partial<Options> = {}
): Promise<ModelResponse> => {
  return new Promise((resolve, reject) => {
    getGltfLoader(!!options.useJsDecoder).load(
      url,
      (gltf: GLTF) => {
        resolve({ scene: gltf.scene, animations: gltf.animations })
      },
      onProgress,
      (err: ErrorEvent) => {
        // Add check for useJsDecoder flag on the off chance we end up in an infinite loop
        if (isDracoWASMError(err) && !options.useJsDecoder) {
          console.log('Hit WASM memory error. Falling back to JS decoder')

          options.analytics?.track('modelViewerDecoderFallback', {
            error: err,
            modelId: options.modelId,
            uuid: options.uuid,
          })

          loadGltf(url, onProgress, { ...options, useJsDecoder: true }).then(resolve, reject)
        } else {
          reject(err)
        }
      }
    )
  })
}
